import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import SortHeader from "../../Components/SortHeader";

// Orders carried name/phone/address/email but there was no view grouping
// them by customer - no way to see "this number has ordered 12 times" or
// pull up someone's history when they call in. Backed by a server-side
// aggregation (routes/ordersCRUD.js GET /customers) rather than fetching
// every order client-side to group them.
const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("lastOrderAt");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const token = localStorage.getItem("authToken");

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/order/customers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: 20, search: search || undefined, sortBy, sortDir },
      });
      setCustomers(response.data.customers);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error("Error fetching customers:", err);
      setLoadError("Couldn't load customers. Check your connection and try again.");
      setCustomers([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, sortBy, sortDir]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
    setPage(1);
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4 text-ink">Customers</h2>

      <input
        placeholder="Search by name or phone…"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
        className="w-full max-w-sm p-2 border border-line rounded mb-4"
      />

      {loadError && (
        <div className="flex items-center justify-between gap-3 mb-4 p-3 border border-red-200 bg-red-50 text-red-700 text-sm rounded">
          <span>{loadError}</span>
          <button onClick={fetchCustomers} className="font-semibold underline shrink-0">
            Retry
          </button>
        </div>
      )}

      <div className="bg-card border border-line rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-paper text-left text-xs uppercase tracking-wide text-muted border-b border-line">
            <tr>
              <SortHeader label="Name" field="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <th className="p-3">Phone</th>
              <SortHeader label="Orders" field="orderCount" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Total Spent" field="totalSpent" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Last Order" field="lastOrderAt" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {loading ? (
              <tr>
                <td className="p-3 text-sub" colSpan={5}>
                  Loading…
                </td>
              </tr>
            ) : loadError ? null : customers.length === 0 ? (
              <tr>
                <td className="p-3 text-sub" colSpan={5}>
                  No customers found.
                </td>
              </tr>
            ) : (
              customers.map((customer) => (
                <tr key={customer.phone} className="hover:bg-paper/60 transition">
                  <td className="p-3 text-ink">{customer.name}</td>
                  <td className="p-3 text-sub font-mono">{customer.phone}</td>
                  <td className="p-3 text-ink">{customer.orderCount}</td>
                  <td className="p-3 text-ink font-mono">
                    {customer.totalSpent > 0 ? `৳${customer.totalSpent}` : "—"}
                  </td>
                  <td className="p-3 text-sub">
                    {new Date(customer.lastOrderAt).toLocaleDateString("en-BD", {
                      timeZone: "Asia/Dhaka",
                    })}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && pagination.totalCount > 0 && (
        <div className="flex items-center gap-3 mt-4">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 border border-line rounded disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-sub">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 border border-line rounded disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Customers;
